import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { PageHeader } from "../PageHeader";
import { QueueTable } from "../shared/QueueTable";
import { FilterChips } from "../shared/FilterChips";
import {
  securityRoomStore,
  selectBlocksList,
  selectObserved24hCount,
} from "../../../stores/securityRoomStore";

type SevFilter = "all" | "deny" | "crit";

function firstString(item: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = item[k];
    if (typeof v === "string" && v) return v;
  }
  return undefined;
}

function matchesSeverity(item: Record<string, unknown>, target: SevFilter): boolean {
  if (target === "all") return true;
  const sev = (item.severity ?? item.decision) as string | undefined;
  return sev === target;
}

// The header shows a passive "Live" pill, not a Refresh button. The page is fed
// by a reactive Convex subscription, so there is nothing to re-fetch — and the
// old button called window.location.reload(), tearing down every live terminal
// and WebSocket in the app to "refresh" data that was already current.
export function BlocksPage() {
  const snap = securityRoomStore.useStore();
  const all = selectBlocksList(snap);
  const [sev, setSev] = useState<SevFilter>("all");

  const filtered = all.filter((e) => matchesSeverity(e, sev));
  // Observe-mode rows are listed but must not be presented as things the engine
  // stopped — they ran.
  const observed = selectObserved24hCount(snap);
  const enforced = all.length - observed;

  return (
    <>
      <PageHeader
        title="Blocks"
        subtitle={
          observed > 0
            ? `${enforced} blocked · ${observed} would block (observe mode) · last 24h`
            : `${enforced} blocks in the last 24h`
        }
        actions={
          <span className="sr-live-pill" title="Updates live via Convex subscription">
            <RefreshCw size={14} aria-hidden="true" /> Live
          </span>
        }
      />
      <div className="sr-page-body">
        <FilterChips
          label="Severity"
          value={sev}
          onChange={setSev}
          options={[
            { value: "all", label: "All" },
            { value: "deny", label: "Deny" },
            { value: "crit", label: "Critical" },
          ]}
        />
        <QueueTable
          events={filtered.map((raw, i) => {
            const e = raw as Record<string, unknown>;
            const id = typeof e.id === "string" ? e.id : `block-${i}`;
            const ts = firstString(e, ["recordedAt", "lastSeenAt", "createdAt", "ts", "at"]) ?? "";
            const rawSev = (e.severity ?? e.decision) as string | undefined;
            const severity = rawSev === "crit" || rawSev === "deny" ? rawSev : ("deny" as const);
            const title = firstString(e, ["title", "reason", "command", "type"]) ?? "Blocked";
            const who = firstString(e, ["agent", "who"]);
            const rule = firstString(e, ["rule", "ruleId"]);
            const cmd = firstString(e, ["command", "cmd"]);
            return { id, ts, severity, title, who, rule, cmd };
          })}
          emptyMessage="No blocks match the current filter"
        />
      </div>
    </>
  );
}
